import AuthLayout from "./AuthLayout";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@mui/material";
import OTPInput from "react-otp-input";
import React, { useState, useEffect } from "react";
import { useVerifyOtpMutation, useResendOtpMutation } from "../../services/auth";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../hooks/store";
import { setAuth } from "../../reducers/authSlice";
import { clearSignupDraft } from "../../utils/authDraft";
import { formatOrSplitPhoneNumber } from "../../utils/profile";

const OtpVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [otp, setOtp] = useState<string>("");
  const [resetKey, setResetKey] = useState<number>(0);
  const [timeLeft, setTimeLeft] = useState<number>(60);
  const [verifyOtp, { isLoading }] = useVerifyOtpMutation();
  const [resendOtp, { isLoading: isResending }] = useResendOtpMutation();

  const {
    email,
    phone_number,
    country_code,
    signupMethod,
    flow,
    first_name,
    last_name,
    password,
    confirm_password,
  } = (location.state as any) || {};
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!email && !phone_number) {
      toast.error("No user information found. Please sign up or log in again.");
      navigate(flow === "login" ? "/" : "/signup");
    }
  }, [email, phone_number, flow, navigate]);

  useEffect(() => {
    if (timeLeft > 0) {
      const timerId = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timerId);
    }
  }, [timeLeft]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleBack = () => {
    if (flow === "forgot_password") {
      navigate("/forgot-password", { state: { email } });
    } else if (flow === "login" || (!first_name && !password && signupMethod === "phone")) {
      navigate("/");
    } else {
      navigate("/signup", {
        state: {
          email,
          phone_number,
          country_code,
          first_name,
          last_name,
          password,
          confirm_password,
        }
      });
    }
  };

  const resetOtpFields = () => {
    setOtp("");
    setResetKey(prev => prev + 1);
    setTimeout(() => {
      const firstInput = document.querySelector(".opt_fields input") as HTMLInputElement;
      if (firstInput) firstInput.focus();
    }, 50);
  };

  const extractToken = (res: any) => {
    return res?.access_token ||
      res?.data?.access_token ||
      res?.access ||
      res?.data?.access ||
      res?.token?.access ||
      res?.data?.token?.access ||
      res?.token ||
      res?.data?.token;
  };

  const extractUserData = (res: any) => {
    return (res?.data?.uuid ? res.data : null) ||
      (res?.data?.data?.uuid ? res.data.data : null) ||
      res?.data?.user ||
      res?.user ||
      res?.data ||
      {};
  };

  const handleVerificationSuccess = (res: any) => {
    if (flow === "forgot_password") {
      toast.success("OTP verified successfully! Please reset your password.");
      navigate("/reset-password", { state: { email, otp } });
      return;
    }

    clearSignupDraft();
    const token = extractToken(res);

    if (!token) {
      toast.error("Failed to authenticate. Token not received.");
      return;
    }

    const userData = extractUserData(res);
    dispatch(setAuth({ token, userData }));
    toast.success(res?.message || "OTP verified successfully! Logged in.");
    navigate("/dashboard");
  };


  const handleVerificationError = (err: any) => {
    let errorMsg = err?.data?.message || err?.message || "Failed to verify OTP";
    const lowerMsg = errorMsg.toLowerCase();

    if (lowerMsg.includes("invalid") || lowerMsg.includes("incorrect")) {
      errorMsg = "The OTP you entered is invalid.";
    } else if (lowerMsg.includes("expire")) {
      errorMsg = "The OTP has expired. Please request a new one.";
    }

    resetOtpFields();
    toast.error(errorMsg);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (timeLeft <= 0) {
      toast.error("OTP has expired. Please resend a new OTP.");
      resetOtpFields();
      return;
    }

    if (otp.length !== 4) {
      toast.error("Please enter a valid 4-digit OTP");
      return;
    }

    try {
      const payload: any = { otp };
      if (email && (signupMethod === "email" || flow === "forgot_password")) payload.email = email;
      if (phone_number && signupMethod === "phone") {
        const cleanPhone = formatOrSplitPhoneNumber(phone_number, country_code);
        payload.phone_number = cleanPhone.phone_number.replace(/^\+/, "").replace(/\D/g, "");
        if (cleanPhone.country_code || country_code) {
          payload.country_code = cleanPhone.country_code || country_code;
        }
      }

      const res = await verifyOtp(payload).unwrap();
      handleVerificationSuccess(res);
    } catch (err: any) {
      handleVerificationError(err);
    }
  };

  const handleResend = async () => {
    if (timeLeft > 0) return;
    try {
      const payload: any = {};
      if (email && (signupMethod === "email" || flow === "forgot_password")) payload.email = email;
      if (phone_number && signupMethod === "phone") {
        const cleanPhone = formatOrSplitPhoneNumber(phone_number, country_code);
        payload.phone_number = cleanPhone.phone_number.replace(/^\+/, "").replace(/\D/g, "");
        if (cleanPhone.country_code || country_code) {
          payload.country_code = cleanPhone.country_code || country_code;
        }
      }

      await resendOtp(payload).unwrap();
      setTimeLeft(60);
      setOtp("");
      setResetKey(prev => prev + 1);
      setTimeout(() => {
        const firstInput = document.querySelector(".opt_fields input") as HTMLInputElement;
        if (firstInput) firstInput.focus();
      }, 50);
      toast.success("OTP resent successfully!");
    } catch (err: any) {
      toast.error(err?.data?.message || err?.message || "Failed to resend OTP");
    }
  };

  return (
    <AuthLayout
      subtitle={flow === "forgot_password" ? "Let's update your password." : undefined}
      title={flow === "forgot_password" ? <React.Fragment>To improve your security,<br />please set a new password.</React.Fragment> : undefined}
    >
      <div className="authBox otpBox">
        <h2 style={{ position: "relative" }}>
          <img
            src="/static/images/back_arrow.svg"
            alt="back"
            onClick={handleBack}
            className="back_arrow"
            style={{ position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)", cursor: "pointer", width: 24, height: 24 }}
          />
          Verify {flow === "forgot_password" ? "Your Email" : (signupMethod === "email" ? "Email" : "Phone No")}
        </h2>
        <p className="subtitle" style={{ marginBottom: 40 }}>
          We have sent a verification OTP to your {flow === "forgot_password" ? "Email" : (signupMethod === "email" ? "email address" : "Phone No")}.<br />
          Kindly check your inbox to retrieve the code.
        </p>
        <form className="form" onSubmit={handleSubmit}>
          <div className="control_group">
            <div className="opt_fields">
              <OTPInput
                key={resetKey}
                value={otp}
                onChange={setOtp}
                numInputs={4}
                renderInput={(props) => <input {...props} />}
                inputType="tel"
                shouldAutoFocus
                containerStyle={{ gap: "20px", justifyContent: "center" }}
              />
            </div>
            <div className="otp_footer">
              <span className="timer">{formatTime(timeLeft)}</span>
              <button
                type="button"
                className="resend_link"
                disabled={timeLeft > 0 || isResending}
                onClick={handleResend}
                style={{ cursor: (timeLeft > 0 || isResending) ? "not-allowed" : "pointer", opacity: (timeLeft > 0 || isResending) ? 0.5 : 1 }}
              >
                {isResending ? "Resending..." : "Resend"}
              </button>
            </div>
          </div>
          <div className="form_btn">
            <Button
              type="submit"
              className="btn btn_primary w_100"
              disabled={isLoading}
            >
              {isLoading ? "Verifying..." : "Submit"}
            </Button>
          </div>
        </form>
      </div>
    </AuthLayout>
  );
};

export default OtpVerification;
